import { useMemo } from 'react';
import { Card, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import type { flows as flowsNs } from '@repletoryreach/sdk-api';
import { useT } from '../../../../i18n/I18nProvider';
import { flowToGraph, chainOrder, TRIGGER_NODE_ID } from '../../builder/mapper';

type Flow = NonNullable<Awaited<ReturnType<typeof flowsNs.getFlow>>['data']>;

/** What each node type does, in one word and one colour. */
const KIND: Record<string, { tint: string; icon: string }> = {
  trigger:   { tint: 'var(--rr-color-success, #16a34a)', icon: '⚡' },
  llm:       { tint: '#8b5cf6', icon: '🤖' },
  reply:     { tint: '#0ea5e9', icon: '💬' },
  send:      { tint: '#0ea5e9', icon: '💬' },
  ask:       { tint: '#14b8a6', icon: '❓' },
  form:      { tint: '#0f766e', icon: '📋' },
  booking:   { tint: '#0e7490', icon: '📅' },
  manage_booking: { tint: '#0369a1', icon: '🗓' },
  ticket:    { tint: '#b45309', icon: '🎫' },
  ticket_status: { tint: '#92400e', icon: '🔎' },
  language:  { tint: '#4f46e5', icon: '🗣' },
  http:      { tint: '#2563eb', icon: '🌐' },
  lookup:    { tint: '#0f766e', icon: '🔍' },
  contact:   { tint: '#f97316', icon: '👤' },
  handoff:   { tint: 'var(--rr-color-warning, #f59e0b)', icon: '🙋' },
  notify:    { tint: '#dc2626', icon: '🔔' },
  sms:       { tint: '#0891b2', icon: '📱' },
  email:     { tint: '#7c3aed', icon: '✉' },
  flow:      { tint: '#0f766e', icon: '↪' },
  set:       { tint: '#64748b', icon: '{ }' },
  condition: { tint: '#eab308', icon: '⑂' },
  decision:  { tint: '#d97706', icon: '◇' },
  template:  { tint: '#0284c7', icon: '📄' },
  switch:    { tint: '#ca8a04', icon: '⇶' },
  hours:     { tint: '#0d9488', icon: '🕒' },
  split:     { tint: '#a855f7', icon: '⚖' },
  delay:     { tint: '#ec4899', icon: '⏱' },
  end:       { tint: '#ef4444', icon: '■' }
};

/**
 * What this flow actually does, as a chain.
 *
 * The detail page answered "what is this called and when did it change?" and
 * nothing on it answered "what happens when it runs?", although the definition
 * was right there to be read. Reading raw JSON to find out is a poor deal for
 * the question people open this page with.
 *
 * Built from the same `flowToGraph` the builder uses, so the two cannot
 * disagree about what the definition means.
 */
export function FlowSummary({ flow }: { flow: Flow }) {
  const t = useT();

  const steps = useMemo(() => {
    const graph = flowToGraph(flow.definition);
    // Reading order: the trigger, then the graph walked from it, so a branch's
    // two arms sit next to each other rather than wherever the file had them.
    const trigger = graph.nodes.find((n) => n.id === TRIGGER_NODE_ID);
    const ordered = [...(trigger ? [trigger] : []), ...chainOrder(graph)];
    return ordered.map((n) => ({
      id: n.id,
      kind: n.type,
      label: typeof n.data.name === 'string' && n.data.name.trim() ? n.data.name.trim() : t(`flows.node.${n.type}`, n.type),
      detail: describe(n.type, n.data)
    }));
  }, [flow.definition, t]);

  return (
    <Card withBorder>
      <Stack gap="sm">
        <Title order={4}>{t('flows.view.summary.title')}</Title>

        {steps.length === 0 ? (
          <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('flows.view.summary.empty')}</Text>
        ) : (
          <>
            <div className="rr-flowsummary">
              {steps.map((step, i) => (
                <div className="rr-flowsummary__step" key={step.id}>
                  <div
                    className="rr-flowsummary__card"
                    style={{ ['--rr-row-tint' as string]: KIND[step.kind]?.tint ?? '#64748b' }}
                  >
                    <span className="rr-flowsummary__icon" aria-hidden>{KIND[step.kind]?.icon ?? '•'}</span>
                    <span className="rr-flowsummary__label">{step.label}</span>
                    {step.detail && <span className="rr-flowsummary__detail">{step.detail}</span>}
                  </div>
                  {i < steps.length - 1 && <span className="rr-flowsummary__arrow" aria-hidden>→</span>}
                </div>
              ))}
            </div>
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('flows.view.summary.hint', { count: steps.length })}
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}

/** The one detail that distinguishes two nodes of the same kind. */
function describe(kind: string, data: Record<string, unknown> | undefined): string | null {
  const d = data ?? {};
  if (kind === 'trigger') {
    const keywords = Array.isArray(d.keywords) ? d.keywords.map(String).filter(Boolean) : [];
    const keyword = keywords[0] ?? d.keyword ?? d.trigger_keyword;
    if (typeof keyword === 'string' && keyword) return `\u201c${keyword}\u201d${keywords.length > 1 ? ` +${keywords.length - 1}` : ''}`;
    return typeof d.type === 'string' && d.type !== 'keyword' ? d.type.replace('_', ' ') : null;
  }
  if (kind === 'llm') {
    if (d.mode === 'classify') return 'sort';
    if (d.mode === 'extract') return 'extract';
    return d.kb_scope === 'all' || (Array.isArray(d.kb_ids) && d.kb_ids.length > 0) ? 'knowledge base' : null; // i18n-ignore: short technical tag
  }
  if (kind === 'handoff') return typeof d.when === 'string' ? d.when : null;
  if (kind === 'ask') return typeof d.variable === 'string' && d.variable ? `{{${d.variable}}}` : null;
  if (kind === 'decision') {
    const n = Array.isArray(d.paths) ? d.paths.length : 0;
    return n ? `${n} ⑂` : null;
  }
  if (kind === 'form') {
    const n = Array.isArray(d.fields) ? d.fields.length : 0;
    const saveAs = typeof d.save_as === 'string' && d.save_as ? d.save_as : 'form';
    return n ? `${n} × → {{${saveAs}}}` : null;
  }
  if (kind === 'http') {
    const auth = d.auth && typeof d.auth === 'object' ? String((d.auth as Record<string, unknown>).type ?? '') : '';
    const method = typeof d.method === 'string' ? d.method.toUpperCase() : 'GET';
    return `${method}${auth && auth !== 'none' ? ` · ${auth}` : ''}`;
  }
  if (kind === 'send' || kind === 'reply' || kind === 'end') return typeof d.body === 'string' ? d.body : typeof d.message === 'string' ? d.message : null;
  if (kind === 'http') return typeof d.url === 'string' ? d.url : null;
  // The subject is what tells two emails apart; the address only when it is not the customer's own.
  if (kind === 'email') {
    const subject = typeof d.subject === 'string' ? d.subject.trim() : '';
    const to = typeof d.to === 'string' ? d.to.trim() : '';
    return subject ? (to ? `${subject} → ${to}` : subject) : to || null;
  }
  if (kind === 'booking') return typeof d.service_name === 'string' && d.service_name.trim() ? d.service_name.trim() : null;
  if (kind === 'ticket') {
    const type = typeof d.type_name === 'string' ? d.type_name.trim() : '';
    const subject = typeof d.subject === 'string' ? d.subject.trim() : '';
    return type && subject ? `${type} · ${subject}` : type || subject || null;
  }
  if (kind === 'sms') return typeof d.message === 'string' && d.message.trim() ? d.message.trim() : null;
  return null;
}
